/**
 * Selection modals of the Style page — Figma `Style · Selection modals`
 * (Styles / Colors / Brands, desktop + mobile). Pure helpers: the color
 * families, the brand tabs and the "Add N …" counter.
 */

import type { FairProfileV2PickerItem } from './fair-profile-v2-my-style.data'

/** Figma `Families` chips, in the order of the list (3075:24012). */
export const FAIR_PROFILE_V2_COLOR_FAMILIES = [
  'neutrals',
  'reds',
  'oranges',
  'yellows',
  'greens',
  'blues',
  'purples',
  'pinks',
  'browns',
  'greys',
  'blacks',
] as const

export type FairProfileV2ColorFamily = (typeof FAIR_PROFILE_V2_COLOR_FAMILIES)[number]

/** `Popular` shows the head of the catalogue — the API returns it most-wanted first. */
export const FAIR_PROFILE_V2_POPULAR_BRANDS = 30

function hexToHsl(hex: string): { h: number; s: number; l: number } | null {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!match) return null
  const value = parseInt(match[1], 16)
  const r = ((value >> 16) & 255) / 255
  const g = ((value >> 8) & 255) / 255
  const b = (value & 255) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  const d = max - min
  if (d === 0) return { h: 0, s: 0, l }
  const s = d / (1 - Math.abs(2 * l - 1))
  let h: number
  if (max === r) h = ((g - b) / d) % 6
  else if (max === g) h = (b - r) / d + 2
  else h = (r - g) / d + 4
  h *= 60
  if (h < 0) h += 360
  return { h, s, l }
}

/**
 * The catalogue has no family (BE-144), so the hex decides: very dark is black,
 * unsaturated is neutral (light) or grey, warm and dark is brown, the rest by hue.
 */
export function fairProfileV2ColorFamily(hex: string | undefined): FairProfileV2ColorFamily {
  const hsl = hex ? hexToHsl(hex) : null
  if (!hsl) return 'neutrals'
  const { h, s, l } = hsl
  if (l < 0.13 || (l < 0.22 && s < 0.2)) return 'blacks'
  if (s < 0.12) return l > 0.82 ? 'neutrals' : 'greys'
  // Cream, ivory, bone: very light warm tones read neutral whatever the saturation.
  if (h >= 25 && h < 70 && (l > 0.88 || (l > 0.8 && s < 0.6))) return 'neutrals'
  if (h >= 10 && h < 50 && l < 0.45) return 'browns'
  if (h >= 300 && h < 345) return 'pinks'
  if (h >= 345 || h < 12) return l > 0.72 ? 'pinks' : 'reds'
  if (h < 48) return 'oranges'
  // Olive and army greens sit on the yellow hue but read green (Figma `Greens`).
  if (h < 70) return l < 0.42 ? 'greens' : 'yellows'
  if (h < 170) return 'greens'
  if (h < 255) return 'blues'
  return 'purples'
}

export interface FairProfileV2PickerGroup {
  id: string
  items: FairProfileV2PickerItem[]
}

/** Colors grouped by family in the Figma order; `family` narrows to one. */
export function groupFairProfileV2Colors(
  items: readonly FairProfileV2PickerItem[],
  family: FairProfileV2ColorFamily | 'all' = 'all',
): FairProfileV2PickerGroup[] {
  const byFamily = new Map<FairProfileV2ColorFamily, FairProfileV2PickerItem[]>()
  for (const item of items) {
    const key = fairProfileV2ColorFamily(item.hex)
    if (family !== 'all' && key !== family) continue
    const list = byFamily.get(key) ?? []
    list.push(item)
    byFamily.set(key, list)
  }
  return FAIR_PROFILE_V2_COLOR_FAMILIES.filter((key) => byFamily.has(key)).map((key) => ({
    id: key,
    items: byFamily.get(key) ?? [],
  }))
}

/** `A–Z` (3076:25411): letter groups, anything that is not a letter under `#`. */
export function groupFairProfileV2BrandsAz(
  items: readonly FairProfileV2PickerItem[],
): FairProfileV2PickerGroup[] {
  const sorted = [...items].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
  const groups = new Map<string, FairProfileV2PickerItem[]>()
  for (const item of sorted) {
    const first = item.name
      .trim()
      .charAt(0)
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toUpperCase()
    const key = /^[A-Z]$/.test(first) ? first : '#'
    const list = groups.get(key) ?? []
    list.push(item)
    groups.set(key, list)
  }
  const keys = [...groups.keys()].sort((a, b) => (a === '#' ? 1 : b === '#' ? -1 : a.localeCompare(b)))
  return keys.map((key) => ({ id: key, items: groups.get(key) ?? [] }))
}

/** Multi-select toggle that keeps the pick order. */
export function toggleFairProfileV2Pick(ids: readonly number[], id: number): number[] {
  return ids.includes(id) ? ids.filter((value) => value !== id) : [...ids, id]
}
